import { Box, Button, Grid, IconButton, Typography } from '@mui/material'
import Loading from 'components/Loading/Loading'
import useFetchApi, {
    isLoading,
    isSuccess,
} from 'hooks/useFetchApi/useFetchApi'
import useSubdomain from 'hooks/useSubdomain/useSubdomain'
import { ResetPasswordRequestContract } from 'models'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import userService from 'Services/api/user.service'
import * as yup from 'yup'
import ErrorIcon from '@mui/icons-material/Error'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import { useHistory } from 'react-router-dom'
import ROUTE from 'constant/route'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import OWInput from 'components/OWForm/OWInput'

function RequestResetPasswordForm() {
    const { t } = useTranslation()

    const { control } = useFormContext<ResetPasswordRequestContract>()

    return (
        <Grid container item spacing={1}>
            <Grid container item justifyContent="center">
                <Typography variant="h6">
                    {t('reset_password.label.reset_password_request')}
                </Typography>
            </Grid>
            <Grid container item>
                <OWInput
                    control={control}
                    name="userName"
                    label={t('reset_password.label.username')}
                    fullWidth
                />
            </Grid>
            <Grid container item justifyContent={'center'}>
                <Button type="submit" color="primary" disableElevation>
                    {t('reset_password.label.submit')}
                </Button>
            </Grid>
        </Grid>
    )
}

const RequestResetPasswordPage = () => {
    const { t } = useTranslation()

    const { fullUrl } = useSubdomain()
    const history = useHistory()

    const [, requestForResetPassword, status, error] = useFetchApi(
        userService.requestForResetPassword,
    )

    const form = useForm<ResetPasswordRequestContract>({
        defaultValues: {
            userName: '',
            resetPasswordUrl: `${fullUrl}${ROUTE.RESET_PASSWORD}`,
        },
        resolver: yupResolver(
            yup.object({
                userName: yup.string().required('Username is required.'),
            }),
        ),
    })

    const { handleSubmit } = form

    const onSubmit = useCallback(
        async (value: ResetPasswordRequestContract) => {
            await requestForResetPassword(value)
        },
        [requestForResetPassword],
    )

    const RequestResetPasswordContainer = () => {
        if (isLoading(status)) return <Loading />

        if (error)
            return (
                <>
                    <Grid container item justifyContent={'center'}>
                        <Typography color="error">
                            <ErrorIcon fontSize="large" />
                        </Typography>
                    </Grid>
                    <Grid container item justifyContent={'center'}>
                        <Typography>{t(error?.message || '')}</Typography>
                    </Grid>
                </>
            )

        if (isSuccess(status))
            return (
                <>
                    <Grid container item justifyContent={'center'}>
                        <Typography color="primary">
                            <CheckCircleIcon fontSize="large" />
                        </Typography>
                    </Grid>
                    <Grid container item justifyContent={'center'}>
                        <Typography>
                            {t('reset_password.label.request_has_sent', {
                                type: 'email',
                            })}
                        </Typography>
                    </Grid>
                </>
            )

        return <RequestResetPasswordForm />
    }

    return (
        <FormProvider {...form}>
            <Box
                sx={{
                    position: 'fixed',
                    overflow: 'auto',
                    background:
                        'radial-gradient(circle, rgba(90, 223, 180, 1) 0%, rgba(18, 174, 133, 1) 50%, rgba(16, 172, 132, 1) 100%)',
                    left: 0,
                    bottom: 0,
                    right: 0,
                    top: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Grid
                    container
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                    width={600}
                    p={4}
                    sx={{
                        backgroundColor: (theme) => theme.palette.topside.paper,
                        borderRadius: 12,
                        position: 'relative',
                    }}
                    spacing={2}
                    justifyContent="center"
                >
                    <Grid sx={{ position: 'absolute', top: 10 }} item container>
                        <IconButton onClick={() => history.push(ROUTE.LOGIN)}>
                            <ArrowBackIosIcon />
                        </IconButton>
                    </Grid>
                    <RequestResetPasswordContainer />
                </Grid>
            </Box>
        </FormProvider>
    )
}

export default RequestResetPasswordPage
